/**
 * Exam simulation API client.
 *
 * Frontend library for interacting with exam simulation backend endpoints.
 * Handles fetching simulation settings based on level and exam type.
 *
 * @module examApi
 */

import { apiGet, apiPost } from './apiUtils';
import { API_BASE_URL, getAuthHeaders } from './apiConfig';
import { buildQueryString } from './utils';
import { logger } from './logger';
import {
  notifySessionInvalidated,
  shouldInvalidateSession,
} from './sessionInvalidation';
import type {
  SimulationSettings,
  CEFRLevel,
  ExamType,
  ExamModule,
  ModuleIntroduction,
  SimulationData,
  BatchUserAnswersRequest,
  BatchUserAnswersResponse,
  SimulationResultSubmission,
  SimulationResultResponse,
} from '@/types/exam-simulation';
import { useExamSimulationStore } from '@/stores/useExamSimulationStore';

// ============================================================================
// API Functions
// ============================================================================

/**
 * Result of requesting authorization to start a mock exam.
 *
 * - `ok`: the mock may proceed (a mock has been consumed against the plan quota)
 * - `upgradeRequired`: the plan blocked the start (quota/level/provider)
 * - `message`: human-readable reason from the backend
 */
export interface StartSimulationResult {
  ok: boolean;
  upgradeRequired: boolean;
  message?: string;
  attemptToken?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readLogoutRequired(data: unknown): boolean | undefined {
  if (!isRecord(data)) {
    return undefined;
  }
  return typeof data.logoutRequired === 'boolean' ? data.logoutRequired : undefined;
}

function readUpgradeRequired(data: unknown, status: number): boolean {
  if (isRecord(data) && isRecord(data.details) && data.details.upgrade_required === true) {
    return true;
  }
  return status === 403 && readLogoutRequired(data) === false;
}

/**
 * Ask the backend to authorize starting a mock exam under the user's plan.
 *
 * Enforces the mock-exam session quota and locked level/provider. The quota
 * counts distinct sessions, so passing the session id lets the backend treat a
 * continued/re-entered exam as the same mock (no extra quota consumed).
 *
 * @param level - CEFR level being started
 * @param examType - Exam provider
 * @param modules - Selected modules for this exam (per-module quota is enforced)
 * @param sessionId - Session id for this exam (stable across its modules)
 * @returns Whether the mock may proceed, with an upgrade flag and message.
 */
export const startSimulationAttempt = async (
  level: CEFRLevel,
  examType: ExamType,
  modules: readonly ExamModule[],
  sessionId?: string | null,
): Promise<StartSimulationResult> => {
  const params = new URLSearchParams({ level, examType });
  if (modules.length > 0) {
    params.set('modules', modules.join(','));
  }
  if (sessionId) {
    params.set('session_id', sessionId);
  }
  try {
    const response = await fetch(`${API_BASE_URL}/simulation/start?${params.toString()}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
    });

    if (response.ok) {
      const data = (await response.json()) as { attempt_token?: unknown };
      return {
        ok: true,
        upgradeRequired: false,
        attemptToken:
          typeof data.attempt_token === 'string' ? data.attempt_token : undefined,
      };
    }

    let message: string | undefined;
    let upgradeRequired = false;
    let logoutRequired: boolean | undefined;
    try {
      const data = await response.json();
      message = data?.error || data?.message;
      logoutRequired = readLogoutRequired(data);
      upgradeRequired = readUpgradeRequired(data, response.status);
    } catch {
      // ignore JSON parse failures
    }
    if (shouldInvalidateSession(response.status, logoutRequired)) {
      notifySessionInvalidated({
        status: response.status,
        message,
        context: 'startSimulationAttempt',
      });
    }
    return { ok: false, upgradeRequired, message };
  } catch (error) {
    logger.error('Simulation start request failed', error);
    return { ok: false, upgradeRequired: false };
  }
};

function currentSimulationAttempt() {
  const { attemptToken, sessionId } = useExamSimulationStore.getState();
  return {
    sessionId,
    headers: attemptToken
      ? { 'X-Simulation-Attempt': attemptToken }
      : undefined,
  };
}

/**
 * Fetch all simulation settings
 *
 * Optionally filters by level and exam type. The backend currently returns
 * all settings regardless of query parameters, but parameters are included
 * for future filtering support.
 *
 * @param level - Optional CEFR level filter (e.g., "A1", "B2")
 * @param examType - Optional exam type filter (e.g., "goethe", "osd")
 * @returns Promise resolving to array of simulation settings
 *
 * @example
 * ```typescript
 * // Fetch all settings
 * const settings = await fetchSimulationSettings();
 *
 * // Fetch settings for specific level and exam type
 * const settings = await fetchSimulationSettings('B2', 'goethe');
 * ```
 */
export const fetchSimulationSettings = async (
  level?: CEFRLevel,
  examType?: ExamType,
): Promise<SimulationSettings[]> => {
  // Note: Backend doesn't currently filter by examType via query params
  // We filter on the client side instead
  const queryString = buildQueryString({
    level,
  });

  const url = queryString ? `/simulation/settings?${queryString}` : '/simulation/settings';

  const result = await apiGet<SimulationSettings[]>(url, {
    errorMessage: 'Failed to fetch simulation settings',
    context: 'fetchSimulationSettings',
    errorReturnValue: [],
  });

  if (!result) {
    return [];
  }

  // Filter by level and examType on client side if provided
  // (backend doesn't currently filter, but we do it here for consistency)
  let filtered = result;
  if (level) {
    filtered = filtered.filter((setting) => setting.level === level);
  }
  if (examType) {
    filtered = filtered.filter((setting) => setting.examType === examType);
  }

  return filtered;
};

/**
 * Find simulation settings for a specific level and exam type
 *
 * @param level - CEFR level (e.g., "A1", "B2")
 * @param examType - Exam type (e.g., "goethe", "osd", "telc")
 * @returns Promise resolving to simulation settings or null if not found
 *
 * @example
 * ```typescript
 * const settings = await findSimulationSettings('B2', 'goethe');
 * if (settings) {
 *   logger.debug('Found settings:', settings);
 * }
 * ```
 */
export const findSimulationSettings = async (
  level: CEFRLevel,
  examType: ExamType,
): Promise<SimulationSettings | null> => {
  const settings = await fetchSimulationSettings(level, examType);
  return settings.find((s) => s.level === level && s.examType === examType) || null;
};

/**
 * Fetch module introduction from database
 *
 * Fetches the introduction content for a specific module, exam type, and level
 * from the database. Used to display module introduction screen before starting
 * each module.
 *
 * @param module - Module name (e.g., "reading", "listening")
 * @param examType - Exam type (e.g., "goethe", "osd", "telc")
 * @param level - CEFR level (e.g., "A1", "B2")
 * @returns Promise resolving to module introduction or null if not found
 *
 * @example
 * ```typescript
 * const intro = await fetchModuleIntroduction('reading', 'goethe', 'A1');
 * if (intro) {
 *   logger.debug('Introduction:', intro.title);
 * }
 * ```
 */
export const fetchModuleIntroduction = async (
  module: ExamModule,
  examType: ExamType,
  level: CEFRLevel,
): Promise<ModuleIntroduction | null> => {
  const params = new URLSearchParams({
    module,
    exam_type: examType,
    level,
  });

  const url = `/admin/introductions/for-simulation?${params.toString()}`;

  try {
    const result = await apiGet<ModuleIntroduction | null>(url, {
      errorMessage: 'Failed to fetch module introduction',
      context: 'fetchModuleIntroduction',
    });

    return result;
  } catch (error) {
    logger.warn('Module introduction not found, using defaults', { error });
    return null;
  }
};

/**
 * Fetch reading module data from backend
 *
 * Fetches the complete simulation data for a reading module including
 * sections, contexts, and questions. Uses POST request with section settings
 * and user ID in the request body.
 *
 * @param level - CEFR level (e.g., "A1", "B2")
 * @param examType - Exam type (e.g., "goethe", "osd", "telc")
 * @param sectionSettings - Section settings from simulation settings
 * @param userId - Optional user ID for tracking
 * @returns Promise resolving to simulation data
 *
 * @example
 * ```typescript
 * const data = await fetchReadingModuleData('A1', 'goethe', settings.sectionSettings, user?.id);
 * ```
 */
export const fetchReadingModuleData = async (
  level: CEFRLevel,
  examType: ExamType,
  sectionSettings: Record<string, unknown>,
  userId?: string | null,
  pinnedQuestionIds?: string[] | null,
): Promise<SimulationData | null> => {
  logger.event(
    'simulation.module.fetch.started',
    {
      module: 'reading',
      level,
      examType,
      userId,
    },
    'info',
    'Reading simulation fetch started',
  );
  const params = new URLSearchParams({
    level,
    examType,
    module: 'reading',
  });

  const url = `/simulation/questions?${params.toString()}`;
  const attempt = currentSimulationAttempt();

  const body = {
    sectionSettings,
    user_id: userId || null,
    session_id: attempt.sessionId,
    // Resume flow: pin the exact question set so it matches what the learner
    // was answering. Omitted on fresh runs to keep adaptive randomized selection.
    ...(pinnedQuestionIds && pinnedQuestionIds.length > 0
      ? { pinnedQuestionIds }
      : {}),
  };

  const result = await apiPost<SimulationData>(url, body, {
    errorMessage: 'Failed to fetch reading module data',
    context: 'fetchReadingModuleData',
    customHeaders: attempt.headers,
  });

  logger.event(
    result ? 'simulation.module.fetch.succeeded' : 'simulation.module.fetch.failed',
    {
      module: 'reading',
      level,
      examType,
      userId,
      hasData: !!result,
    },
    result ? 'info' : 'warn',
    result ? 'Reading simulation fetch succeeded' : 'Reading simulation fetch failed',
  );
  return result;
};

/**
 * Fetch listening module data from backend
 *
 * Fetches the complete simulation data for a listening module including
 * sections, contexts, and questions. Uses POST request with section settings
 * and user ID in the request body.
 *
 * @param level - CEFR level (e.g., "A1", "B2")
 * @param examType - Exam type (e.g., "goethe", "osd", "telc")
 * @param sectionSettings - Section settings from simulation settings
 * @param userId - Optional user ID for tracking
 * @returns Promise resolving to simulation data
 *
 * @example
 * ```typescript
 * const data = await fetchListeningModuleData('A1', 'goethe', settings.sectionSettings, user?.id);
 * ```
 */
export const fetchListeningModuleData = async (
  level: CEFRLevel,
  examType: ExamType,
  sectionSettings: Record<string, unknown>,
  userId?: string | null,
  pinnedQuestionIds?: string[] | null,
): Promise<SimulationData | null> => {
  logger.event(
    'simulation.module.fetch.started',
    {
      module: 'listening',
      level,
      examType,
      userId,
    },
    'info',
    'Listening simulation fetch started',
  );
  const params = new URLSearchParams({
    level,
    examType,
    module: 'listening',
  });

  const url = `/simulation/questions?${params.toString()}`;
  const attempt = currentSimulationAttempt();

  const body = {
    sectionSettings,
    user_id: userId || null,
    session_id: attempt.sessionId,
    // Resume flow: pin the exact question set so it matches what the learner
    // was answering. Omitted on fresh runs to keep adaptive randomized selection.
    ...(pinnedQuestionIds && pinnedQuestionIds.length > 0
      ? { pinnedQuestionIds }
      : {}),
  };

  const result = await apiPost<SimulationData>(url, body, {
    errorMessage: 'Failed to fetch listening module data',
    context: 'fetchListeningModuleData',
    customHeaders: attempt.headers,
  });

  logger.event(
    result ? 'simulation.module.fetch.succeeded' : 'simulation.module.fetch.failed',
    {
      module: 'listening',
      level,
      examType,
      userId,
      hasData: !!result,
    },
    result ? 'info' : 'warn',
    result ? 'Listening simulation fetch succeeded' : 'Listening simulation fetch failed',
  );
  return result;
};

/**
 * Fetch speaking module data from backend
 *
 * Fetches the complete simulation data for a speaking module including
 * sections, contexts, and questions. Uses POST request with section settings
 * and user ID in the request body.
 */
export const fetchSpeakingModuleData = async (
  level: CEFRLevel,
  examType: ExamType,
  sectionSettings: Record<string, unknown>,
  userId?: string | null,
  pinnedQuestionIds?: string[] | null,
): Promise<SimulationData | null> => {
  logger.event(
    'simulation.module.fetch.started',
    {
      module: 'speaking',
      level,
      examType,
      userId,
    },
    'info',
    'Speaking simulation fetch started',
  );
  const params = new URLSearchParams({
    level,
    examType,
    module: 'speaking',
  });

  const url = `/simulation/questions?${params.toString()}`;
  const attempt = currentSimulationAttempt();

  const body = {
    sectionSettings,
    user_id: userId || null,
    session_id: attempt.sessionId,
    // Resume flow: pin the exact question set so it matches what the learner
    // was answering. Omitted on fresh runs to keep adaptive randomized selection.
    ...(pinnedQuestionIds && pinnedQuestionIds.length > 0
      ? { pinnedQuestionIds }
      : {}),
  };

  const result = await apiPost<SimulationData>(url, body, {
    errorMessage: 'Failed to fetch speaking module data',
    context: 'fetchSpeakingModuleData',
    customHeaders: attempt.headers,
  });

  logger.event(
    result ? 'simulation.module.fetch.succeeded' : 'simulation.module.fetch.failed',
    {
      module: 'speaking',
      level,
      examType,
      userId,
      hasData: !!result,
    },
    result ? 'info' : 'warn',
    result ? 'Speaking simulation fetch succeeded' : 'Speaking simulation fetch failed',
  );
  return result;
};

/**
 * Fetch writing module data from backend
 *
 * Fetches the complete simulation data for a writing module including
 * sections, contexts, and questions. Uses POST request with section settings
 * and user ID in the request body.
 *
 * @param level - CEFR level (e.g., "A1", "B2")
 * @param examType - Exam type (e.g., "goethe", "osd", "telc")
 * @param sectionSettings - Section settings from simulation settings
 * @param userId - Optional user ID for tracking
 * @returns Promise resolving to simulation data
 *
 * @example
 * ```typescript
 * const data = await fetchWritingModuleData('A1', 'goethe', settings.sectionSettings, user?.id);
 * ```
 */
export const fetchWritingModuleData = async (
  level: CEFRLevel,
  examType: ExamType,
  sectionSettings: Record<string, unknown>,
  userId?: string | null,
  pinnedQuestionIds?: string[] | null,
): Promise<SimulationData | null> => {
  logger.event(
    'simulation.module.fetch.started',
    {
      module: 'writing',
      level,
      examType,
      userId,
    },
    'info',
    'Writing simulation fetch started',
  );
  const params = new URLSearchParams({
    level,
    examType,
    module: 'writing',
  });

  const url = `/simulation/questions?${params.toString()}`;
  const attempt = currentSimulationAttempt();

  const body = {
    sectionSettings,
    user_id: userId || null,
    session_id: attempt.sessionId,
    // Resume flow: pin the exact task(s) the learner saw so a reshuffle can't
    // swap in a different essay/fill-in-blank alternate. Omitted on fresh runs.
    ...(pinnedQuestionIds && pinnedQuestionIds.length > 0
      ? { pinnedQuestionIds }
      : {}),
  };

  const result = await apiPost<SimulationData>(url, body, {
    errorMessage: 'Failed to fetch writing module data',
    context: 'fetchWritingModuleData',
    customHeaders: attempt.headers,
  });

  logger.event(
    result ? 'simulation.module.fetch.succeeded' : 'simulation.module.fetch.failed',
    {
      module: 'writing',
      level,
      examType,
      userId,
      hasData: !!result,
    },
    result ? 'info' : 'warn',
    result ? 'Writing simulation fetch succeeded' : 'Writing simulation fetch failed',
  );
  return result;
};

/**
 * Save user answers in batch
 *
 * Saves multiple user answers to the database in a single batch operation.
 * Used when finishing a reading section to save all answers (answered and unanswered).
 *
 * @param answers - Batch request containing array of answer submissions
 * @returns Promise resolving to batch response with recorded answers
 *
 * @example
 * ```typescript
 * const response = await saveUserAnswersBatch({
 *   answers: [
 *     {
 *       user_id: "user-uuid",
 *       session_id: "sess-123",
 *       question_id: "question-uuid",
 *       module: "reading",
 *       section: 1,
 *       answer_text: "a",
 *       is_correct: true,
 *       points: 1.0,
 *     },
 *   ],
 * });
 * ```
 */
export const saveUserAnswersBatch = async (
  answers: BatchUserAnswersRequest,
): Promise<BatchUserAnswersResponse> => {
  const firstAnswer = answers.answers[0];
  logger.event(
    'simulation.answers.save.started',
    {
      module: firstAnswer?.module,
      sessionId: firstAnswer?.session_id,
      userId: firstAnswer?.user_id,
      answerCount: answers.answers.length,
      questionIds: answers.answers.map((answer) => answer.question_id),
    },
    'info',
    'Simulation answers save started',
  );
  const result = await apiPost<BatchUserAnswersResponse>(
    '/user-answers/batch',
    answers,
    {
      errorMessage: 'Failed to save answers',
      context: 'saveUserAnswersBatch',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to save answers');
  }

  logger.event(
    result ? 'simulation.answers.save.succeeded' : 'simulation.answers.save.failed',
    {
      module: firstAnswer?.module,
      sessionId: firstAnswer?.session_id,
      userId: firstAnswer?.user_id,
      answerCount: answers.answers.length,
      hasResult: !!result,
    },
    result ? 'info' : 'error',
    result ? 'Simulation answers save succeeded' : 'Simulation answers save failed',
  );
  return result;
};

/**
 * Save simulation result
 *
 * Saves the overall result for a completed module/section to the database.
 * Includes score, pass/fail status, and metadata.
 *
 * @param result - Simulation result submission data
 * @returns Promise resolving to result response
 *
 * @example
 * ```typescript
 * const response = await saveSimulationResult({
 *   session_id: "sess-123",
 *   user_id: "user-uuid",
 *   module: "reading",
 *   exam_type: "goethe",
 *   level: "A1",
 *   score: 85.5,
 *   passed: true,
 *   is_graded: false,
 * });
 * ```
 */
export const saveSimulationResult = async (
  result: SimulationResultSubmission,
): Promise<SimulationResultResponse> => {
  logger.event(
    'simulation.result.save.started',
    {
      module: result.module,
      sessionId: result.session_id,
      userId: result.user_id,
      examType: result.exam_type,
      level: result.level,
      isGraded: result.is_graded,
      passed: result.passed,
      score: result.score,
    },
    'info',
    'Simulation result save started',
  );
  const response = await apiPost<SimulationResultResponse>(
    '/simulation/results',
    result,
    {
      errorMessage: 'Failed to save simulation result',
      context: 'saveSimulationResult',
      throwOnError: true,
    },
  );
  if (!response) {
    throw new Error('Failed to save simulation result');
  }

  logger.event(
    response ? 'simulation.result.save.succeeded' : 'simulation.result.save.failed',
    {
      module: result.module,
      sessionId: result.session_id,
      userId: result.user_id,
      examType: result.exam_type,
      level: result.level,
      hasResponse: !!response,
    },
    response ? 'info' : 'error',
    response ? 'Simulation result save succeeded' : 'Simulation result save failed',
  );
  return response;
};
